import { Box, Button, Modal, NumberInput, SegmentedControl, Select, Skeleton, Switch, Text, Textarea } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { ArrowRightIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { InvoiceApi } from "../../apis";
import { currencyFormat } from "../../utils";
import { message } from "../../utils/Message";
import { handleError } from "../../utils/error-handler";
import { renderBusinessName } from "../../utils/render-business-name";
import { AppModalHeader, ModalFooter } from "../ui/app-modal";
import modalChrome from "../ui/app-modal.module.css";
import classes from "./extend-payment-modal.module.css";

dayjs.extend(utc);

interface ExtendPaymentModalProps {
  invoiceId: string;
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Optional: the dialog loads the invoice number and due day itself. */
  invoiceNumber?: string;
  currentDueDate?: string;
}

const REASON_CODES = [
  "PARTIAL_PAYMENT_PLAN",
  "BUYER_REQUEST",
  "FINANCIAL_DIFFICULTY",
  "DISPUTE_RESOLUTION",
  "SEASONAL_ADJUSTMENT",
  "RELATIONSHIP_MANAGEMENT",
  "FORCE_MAJEURE",
  "OTHER",
];

const CHIP_DAYS = [3, 7, 14, 30];
const CUSTOM = "custom";

const addDays = (day: string, n: number) => dayjs.utc(day, "YYYY-MM-DD").add(n, "day").format("YYYY-MM-DD");
const daysBetween = (from: string, to: string) => dayjs.utc(to, "YYYY-MM-DD").diff(dayjs.utc(from, "YYYY-MM-DD"), "day");

export function ExtendPaymentModal({ invoiceId, opened, onClose, onSuccess }: ExtendPaymentModalProps) {
  const { t } = useTranslation("common");
  const { t: ti } = useTranslation("invoice");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"DAYS" | "DATE">("DAYS");
  const [chip, setChip] = useState<string>("7");
  const [customDays, setCustomDays] = useState<number | string>(10);
  const [date, setDate] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [resetLateFee, setResetLateFee] = useState(false);

  const { data: ctx, error, isLoading } = useSWR(
    opened && invoiceId ? `invoice.extension.context.${invoiceId}` : null,
    () => InvoiceApi.extensionContext(invoiceId),
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    if (!opened) return;
    setMode("DAYS");
    setChip("7");
    setCustomDays(10);
    setDate(null);
    setReason(null);
    setNotes("");
    setResetLateFee(false);
  }, [opened, invoiceId]);

  const handleClose = () => {
    if (!loading) onClose();
  };

  const maxDays = ctx?.maxExtensionDays ?? 90;
  const today = ctx?.today ?? "";
  const dueDay = ctx?.dueDay ?? null;
  const extraDays = chip === CUSTOM ? Number(customDays) || 0 : Number(chip);

  let newDay: string | null = null;
  if (dueDay) {
    if (mode === "DAYS") newDay = extraDays >= 1 ? addDays(dueDay, extraDays) : null;
    else newDay = date ? date.slice(0, 10) : null;
  }
  const tooEarly = !!newDay && !!today && newDay <= today;
  const fromToday = newDay && today ? daysBetween(today, newDay) : null;

  const limitReached = !!ctx && ctx.extensionCount >= ctx.maxExtensions;
  const blocked = !!ctx && !ctx.canExtend;
  const canSubmit = !!ctx && ctx.canExtend && !!newDay && !tooEarly && !!reason && !loading;

  const overdueCell = (days: number | null) => {
    if (days == null) return null;
    if (days > 0) return <Text span className={`${classes.cellHint} ${classes.cellDanger}`}>{ti("extend.overdue", { count: days })}</Text>;
    if (days < 0) return <Text span className={classes.cellHint}>{ti("extend.left", { count: -days })}</Text>;
    return <Text span className={classes.cellHint}>{ti("extend.dueToday")}</Text>;
  };

  const handleSubmit = async () => {
    if (!canSubmit || !newDay) return;
    try {
      setLoading(true);
      await InvoiceApi.extendPayment(invoiceId, {
        extensionType: mode === "DAYS" ? "ADDITIONAL_DAYS" : "NEW_DUE_DATE",
        additionalDays: mode === "DAYS" ? extraDays : undefined,
        newDueDate: mode === "DATE" ? newDay : undefined,
        reason,
        notes: notes.trim() || undefined,
        resetLateFee: resetLateFee && !!ctx?.lateFee.enabled && ctx.lateFee.accrued > 0,
      });
      message.success(ti("extend.success"));
      onSuccess();
      onClose();
    } catch (e) {
      handleError(e, ti("extend.error"));
    } finally {
      setLoading(false);
    }
  };

  const subtitle = ctx ? (
    <Text span className={classes.sub}>
      <Text span className={classes.subNumber}>{ctx.refCode}</Text>
      {" · "}
      {ti("extend.subtitle", { party: renderBusinessName(ctx.counterparty, "-"), amount: currencyFormat(ctx.amountToPay) })}
    </Text>
  ) : null;

  const monthDay = (d: string) => d.slice(5);
  const weekday = newDay ? ti(`extend.weekday.${dayjs.utc(newDay, "YYYY-MM-DD").day()}`) : "";

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      padding={0}
      size="md"
      zIndex={400}
      classNames={{ body: modalChrome.modalBodyFlush }}
      aria-label={ti("extend.title")}
    >
      <AppModalHeader title={ti("extend.title")} subtitle={subtitle} onClose={handleClose} closeDisabled={loading} />

      <Box className={classes.body}>
        {isLoading && (
          <>
            <Skeleton h={72} />
            <Skeleton h={34} />
            <Skeleton h={40} />
          </>
        )}
        {!isLoading && (error || !ctx) && <Box className={classes.notice}>{ti("extend.loadError")}</Box>}
        {ctx && limitReached && (
          <Box className={classes.notice}>{ti("extend.limitReached", { count: ctx.extensionCount, max: ctx.maxExtensions })}</Box>
        )}
        {ctx && blocked && !limitReached && <Box className={classes.notice}>{ti("extend.notExtendable")}</Box>}

        {ctx && (
          <>
            <Box className={classes.compare} data-invalid={tooEarly || undefined}>
              <Box className={classes.cell}>
                <Text span className={classes.cellLabel}>{ti("extend.current")}</Text>
                <Text span className={classes.cellValue}>{dueDay ?? "-"}</Text>
                {overdueCell(ctx.overdueDays)}
              </Box>
              <ArrowRightIcon size={20} className={classes.arrow} aria-hidden />
              <Box className={classes.cell}>
                <Text span className={classes.cellLabel}>{ti("extend.next")}</Text>
                <Text span className={`${classes.cellValue} ${classes.cellValueNext}`}>
                  {newDay ? `${newDay} · ${weekday}` : "-"}
                </Text>
                {tooEarly ? (
                  <Text span className={`${classes.cellHint} ${classes.cellDanger}`}>
                    {ti("extend.tooEarly", { count: ctx.minAdditionalDays })}
                  </Text>
                ) : fromToday != null ? (
                  <Text span className={classes.cellHint}>{ti("extend.fromToday", { count: fromToday })}</Text>
                ) : (
                  <Text span className={classes.cellHint}>{ti("extend.pickPrompt")}</Text>
                )}
              </Box>
            </Box>

            <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Box className={classes.methodRow}>
                <Text span className={classes.methodLabel}>{mode === "DAYS" ? ti("extend.howMany") : ti("extend.pickDate")}</Text>
                <SegmentedControl
                  size="xs"
                  value={mode}
                  onChange={(v) => setMode(v as "DAYS" | "DATE")}
                  aria-label={ti("extend.modeLabel")}
                  data={[
                    { value: "DAYS", label: ti("extend.modeDays") },
                    { value: "DATE", label: ti("extend.modeDate") },
                  ]}
                />
              </Box>

              {mode === "DAYS" ? (
                <>
                  <Box className={classes.chips}>
                    {CHIP_DAYS.map((d) => (
                      <button
                        key={d}
                        type="button"
                        className={classes.chip}
                        data-active={chip === String(d) || undefined}
                        onClick={() => setChip(String(d))}
                      >
                        +{d}
                      </button>
                    ))}
                    <button type="button" className={classes.chip} data-active={chip === CUSTOM || undefined} onClick={() => setChip(CUSTOM)}>
                      {ti("extend.custom")}
                    </button>
                  </Box>
                  {chip === CUSTOM && (
                    <NumberInput
                      aria-label={ti("extend.customDays", { max: maxDays })}
                      placeholder={ti("extend.customDays", { max: maxDays })}
                      value={customDays}
                      onChange={setCustomDays}
                      min={1}
                      max={maxDays}
                      allowDecimal={false}
                      allowNegative={false}
                    />
                  )}
                  <Text span className={classes.hint}>
                    {ti("extend.daysHint", { date: dueDay ? monthDay(dueDay) : "-", max: maxDays })}
                  </Text>
                </>
              ) : (
                <DatePickerInput
                  aria-label={ti("extend.pickDate")}
                  placeholder={ti("extend.pickPrompt")}
                  value={date}
                  onChange={(v) => setDate(v as string | null)}
                  valueFormat="YYYY-MM-DD"
                  minDate={today ? addDays(today, 1) : undefined}
                  maxDate={today ? addDays(today, maxDays) : undefined}
                  popoverProps={{ zIndex: 500 }}
                />
              )}
            </Box>

            <Select
              label={ti("extend.reason")}
              placeholder={ti("extend.reasonPlaceholder")}
              data={REASON_CODES.map((code) => ({ value: code, label: ti(`extend.reasonOpt.${code}`) }))}
              value={reason}
              onChange={setReason}
              withAsterisk
              comboboxProps={{ zIndex: 500 }}
            />

            <Textarea
              label={ti("extend.notes")}
              placeholder={ti("extend.notesPlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              minRows={2}
              autosize
              maxLength={500}
            />

            {ctx.lateFee.enabled && ctx.lateFee.accrued > 0 && (
              <Box className={classes.waive}>
                <Box className={classes.waiveText}>
                  <Text span className={classes.waiveTitle}>{ti("extend.waiveTitle")}</Text>
                  <Text span className={classes.hint}>{ti("extend.waiveHint", { amount: currencyFormat(ctx.lateFee.accrued) })}</Text>
                </Box>
                <Switch
                  checked={resetLateFee}
                  onChange={(e) => setResetLateFee(e.currentTarget.checked)}
                  aria-label={ti("extend.waiveTitle")}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <ModalFooter>
        <Box className={classes.footerBar}>
          <Text span className={classes.footerNote}>
            {ctx && !limitReached ? ti("extend.willBe", { n: ctx.extensionCount + 1, max: ctx.maxExtensions }) : ""}
          </Text>
          <Box className={classes.footerActions}>
            <Button variant="default" onClick={handleClose} disabled={loading}>
              {t("cancel")}
            </Button>
            <Button loading={loading} disabled={!canSubmit} onClick={handleSubmit}>
              {newDay ? ti("extend.submitUntil", { date: monthDay(newDay) }) : ti("extend.submit")}
            </Button>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
}
